/** @jsxImportSource @emotion/react */
import React, { ComponentType, FC } from 'react';
import { Box, Grid, useMediaQuery } from '@mui/material';
import { breakpoints } from '@zip/business-components';
import { Constants, theme } from 'global';
import * as styles from 'modules/sign-up/SignUpFlow.styles';

export interface WithZipLogoOptions {
  mobileOnly?: boolean;
}

export function withZipLogo<P extends Record<string, any>>(
  WrappedComponent: ComponentType<P>,
  options?: WithZipLogoOptions
): FC<P> {
  const { mobileOnly = false } = options ?? {};

  const WithZipLogo: FC<P> = (props) => {
    const isDesktop = useMediaQuery(breakpoints.up(theme.breaks.mdOnboarding));

    if (mobileOnly) {
      if (!isDesktop) {
        return (
          <>
            <Box css={styles.rightPanelLogo}>
              <img src={Constants.assets.logo.light} alt="Zip logo" />
            </Box>
            <WrappedComponent {...(props as any)} />
          </>
        );
      }
      return <WrappedComponent {...(props as any)} />;
    }

    if (isDesktop) {
      return (
        <>
          <Grid
            container
            columns={12}
            css={styles.topBarGridContainer}
            data-id="logo"
          >
            <Box css={styles.topBar}>
              <a
                href={Constants.zipBusinessPaymentsUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                <img
                  src={Constants.assets.logo.light}
                  width={72}
                  height={32}
                  alt="Zip logo"
                />
              </a>
            </Box>
          </Grid>
          <WrappedComponent {...(props as any)} />
        </>
      );
    }

    return (
      <>
        <Box css={styles.rightPanelLogo}>
          <img src={Constants.assets.logo.light} alt="Zip logo" />
        </Box>
        <WrappedComponent {...(props as any)} />
      </>
    );
  };

  const wrappedName =
    WrappedComponent.displayName || WrappedComponent.name || 'Component';
  WithZipLogo.displayName = `withZipLogo(${wrappedName})`;

  return WithZipLogo;
}

export function withZipLogoOnMobile<P extends Record<string, any>>(
  WrappedComponent: ComponentType<P>
): FC<P> {
  return withZipLogo(WrappedComponent, { mobileOnly: true });
}
